import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings2, GripVertical, Eye, EyeOff, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * ColumnConfig — Dropdown to show/hide columns + resizable headers
 * Usage:
 *   const { visibleCols, colWidths, ColumnToggle, ResizableHeader } = useColumnConfig(columns, storageKey);
 */
export function useColumnConfig(allColumns, storageKey) {
  const saved = localStorage.getItem(`ainaa_cols_${storageKey}`);
  const [hidden, setHidden] = useState(() => {
    try { return new Set(JSON.parse(saved) || []); } catch { return new Set(); }
  });
  const [widths, setWidths] = useState({});

  useEffect(() => {
    localStorage.setItem(`ainaa_cols_${storageKey}`, JSON.stringify([...hidden]));
  }, [hidden, storageKey]);

  const toggleCol = (key) => {
    setHidden(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const resetCols = () => { setHidden(new Set()); setWidths({}); };

  const visibleCols = allColumns.filter(c => !hidden.has(c.key));

  // Resize handler
  const startResize = useCallback((key, startX) => {
    const onMove = (e) => {
      const dx = e.clientX - startX;
      setWidths(prev => ({ ...prev, [key]: Math.max(60, (prev[key] || 120) + dx) }));
      startX = e.clientX;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const ColumnToggle = ({ className = '' }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
      <div ref={ref} className={`relative ${className}`}>
        <button onClick={() => setOpen(!open)}
          className="p-1.5 text-[#9B9589] hover:text-[#0F1B2D] border border-[#D8D3CB] hover:border-[#C4A35A] transition-colors"
          data-testid="column-toggle-btn" title="Show/Hide Columns">
          <Settings2 size={14} />
        </button>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
              className="absolute right-0 top-8 z-50 w-56 bg-white border-2 border-[#0F1B2D] shadow-lg" data-testid="column-toggle-menu">
              <div className="px-3 py-2 border-b border-[#D8D3CB] flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589]">Columns</span>
                <button onClick={resetCols} className="text-[9px] font-bold text-[#C4A35A] hover:text-[#0F1B2D] flex items-center gap-1" data-testid="column-reset-btn">
                  <RotateCcw size={10} /> Reset
                </button>
              </div>
              <div className="p-1 max-h-60 overflow-y-auto">
                {allColumns.map(col => (
                  <button key={col.key} onClick={() => toggleCol(col.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#4A4640] hover:bg-[#EDECEA] transition-colors"
                    data-testid={`col-toggle-${col.key}`}>
                    {hidden.has(col.key) ? <EyeOff size={12} className="text-[#D8D3CB]" /> : <Eye size={12} className="text-emerald-500" />}
                    {col.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const ResizableHeader = ({ col, sortKey, currentSort, onSort, className = '' }) => {
    const w = widths[col.key];
    const active = currentSort?.key === (sortKey || col.key);
    const Icon = !active ? ArrowUpDown : currentSort?.dir === 'asc' ? ArrowUp : ArrowDown;
    const handleClick = () => onSort?.(sortKey || col.key);
    return (
      <th className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] relative select-none cursor-pointer hover:text-[#0F1B2D] transition-colors ${className}`}
        style={w ? { width: `${w}px`, minWidth: `${w}px` } : {}} data-testid={`th-${col.key}`}
        onClick={handleClick}>
        <span className="inline-flex items-center gap-1">
          {col.label}
          {onSort && <Icon size={10} className={active ? 'text-[#C4A35A]' : 'opacity-40'} />}
        </span>
        <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C4A35A]/30 active:bg-[#C4A35A]/50"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startResize(col.key, e.clientX); }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`resize-${col.key}`}>
          <GripVertical size={8} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-[#D8D3CB]" />
        </div>
      </th>
    );
  };

  return { visibleCols, colWidths: widths, ColumnToggle, ResizableHeader, hidden };
}
